import { useEffect, useRef, useState } from 'react';
import { IN_FIELD_ORDER, moveToNextField } from '../lib/field-order';

interface NumberFieldProps {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  label: string;
  placeholder?: string;
  className?: string;
  /** Table-cell size, for the desk's Take Off; the default is sized for a finger on the iPad. */
  compact?: boolean;
  /** A leading minus may be typed (the negotiating factor, where negative is a discount). */
  allowNegative?: boolean;
}

const UNSIGNED = /^\d*\.?\d*$/;
const SIGNED = /^-?\d*\.?\d*$/;
/** Typed so far but not a number yet: blank, a lone point or a lone minus. */
const NOT_A_NUMBER_YET = new Set(['', '.', '-', '-.']);

const toText = (value: number | null | undefined) => (typeof value === 'number' ? String(value) : '');

/**
 * A number typed on the iPad's decimal keypad. Keeps what was typed ("2.")
 * while focused, reports the parsed number (or null when blank) on every
 * keystroke, and follows outside changes when not being edited.
 */
/** Compact boxes take their width from `className` and show the cell's colour through. */
const SIZE = { normal: 'h-11 w-full px-2 text-base bg-white', compact: 'h-7 px-1 text-sm bg-transparent' };

export function NumberField({ value, onChange, label, placeholder, className = '', compact = false, allowNegative = false }: NumberFieldProps) {
  const [text, setText] = useState(toText(value));
  const focused = useRef(false);
  const justTapped = useRef(false);

  /**
   * A tap selects the value, as Return does (#388): typing replaces it.
   * Selecting in onFocus doesn't stick — the tap then puts the cursor where
   * the finger landed (left of right-aligned digits) — so it's done on the
   * click that follows, once per tap into the box.
   */
  function selectAfterTap(input: HTMLInputElement) {
    if (!justTapped.current) return;
    justTapped.current = false;
    input.setSelectionRange(0, input.value.length);
  }

  useEffect(() => {
    if (!focused.current) setText(toText(value));
  }, [value]);

  function change(next: string) {
    const cleaned = next.replace(',', '.');
    if (!(allowNegative ? SIGNED : UNSIGNED).test(cleaned)) return;
    setText(cleaned);
    onChange(NOT_A_NUMBER_YET.has(cleaned) ? null : Number(cleaned));
  }

  return (
    <input
      type="text"
      inputMode="decimal"
      enterKeyHint="next"
      {...IN_FIELD_ORDER}
      onKeyDown={(e) => moveToNextField(e)}
      aria-label={label}
      placeholder={placeholder}
      value={text}
      onPointerDown={(e) => (justTapped.current = document.activeElement !== e.currentTarget)}
      onClick={(e) => selectAfterTap(e.currentTarget)}
      onFocus={() => (focused.current = true)}
      onBlur={() => {
        focused.current = false;
        justTapped.current = false;
        setText(toText(value));
      }}
      onChange={(e) => change(e.target.value)}
      className={`${SIZE[compact ? 'compact' : 'normal']} min-w-0 rounded-md border border-slate-300 text-right tabular-nums focus:border-primary focus:outline-none ${className}`}
    />
  );
}
